import { Pipe, PipeTransform } from '@angular/core';

/**
 * Native-Angular port of the AngularJS `dateRange` filter
 * (account-v1/.../components/common/date-range/dateRange.filter.js):
 *   start | dateRange:end →
 *     "Jan 1 - Jan 5, 2026"            (same-year, short start month-day)
 *     "Dec 28, 2025 - Jan 5, 2026"     (cross-year, full start)
 * Inputs accept Date | epoch-ms | ISO string. UTC-based, matching v1.
 */
@Pipe({
    name: 'dateRange',
    standalone: false
})
export class DateRangePipe implements PipeTransform {
  transform(start: Date | number | string, end: Date | number | string): string {
    const s = toUtcDate(start);
    const e = toUtcDate(end);
    if (!s || !e) return '';
    const sameYear = s.getUTCFullYear() === e.getUTCFullYear();
    const startFmt = sameYear ? fmtShort(s) : fmtFull(s);
    return `${startFmt} - ${fmtFull(e)}`;
  }
}

function toUtcDate(v: Date | number | string): Date | null {
  if (v == null) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

const SHORT_MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtShort(d: Date): string {
  return `${SHORT_MONTH[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function fmtFull(d: Date): string {
  return `${SHORT_MONTH[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

/**
 * Native-Angular port of the AngularJS `daysUntilDate` filter
 * (account-v1/.../components/common/date-range/daysUntilDate.filter.js):
 *   end | daysUntilDate:start  → integer days between start (or now) and end.
 * v1 does `moment(end).diff(moment(start).subtract(1,'days'), 'days')` —
 * the subtract-then-diff adds one to a naive day count (it's inclusive of
 * the start day).
 */
@Pipe({
    name: 'daysUntilDate',
    standalone: false
})
export class DaysUntilDatePipe implements PipeTransform {
  transform(end: Date | number | string, start?: Date | number | string): number {
    const endDate = toUtcDate(end);
    if (!endDate) return 0;
    const startDate = start != null ? toUtcDate(start) : new Date();
    if (!startDate) return 0;
    const oneDay = 24 * 60 * 60 * 1000;
    // +1 mirrors v1's `.subtract('1','days')` before the diff.
    return Math.floor((endDate.getTime() - startDate.getTime()) / oneDay) + 1;
  }
}
